/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Field, FieldTypes, Inscription, validatePhoneNumber, validateUrl } from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { REGEXPS, VALIDATORS } from '@/common';
import { FieldStateKey, NO_KEY, PermissionKey, YES_KEY } from '@/common/constants';
import Permissions from '@/common/permissions';
import { Option } from '@/common/types';
import { IOverridablePropertiesInfoDtoDepositoryBankConfigTypeEnum as  DepositoryBankConfigTypeEnum} from '@/dtos/models/IOverridablePropertiesInfoDto';
import { useHasAccess } from '@/modules/core/hooks';
import { useStore as useErrorStore } from '@/modules/employer/components/SetupEmployer/stores/useServerErrors.store';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { EmployerSetupModes } from '@/modules/employer/employerSetupModes.types';
import { useStore } from '@/modules/employer/store/useSetupEmployer.store';
import { EmployerDto } from '@/modules/employer/types';
import { FeatureFlags, useFeatureFlag } from '@/modules/featureFlags';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';
import { booleanToYesOrNo } from '@/utils/handlers';

import { useHideFields, useSetupEmployerMode } from '../hooks';

export const FIELD_KEY = {
  DBA: 'dba',
  TAX_ID: 'taxID',
  PHONE_NUMBER: 'phoneNumber',
  PHONE_NUMBER_EXTENSION: 'phoneNumberExtension',
  BUSINESS_TYPE: 'businessType',
  ACCOUNT_MANAGER_ID: 'accountManagerId',
  IMPLEMENTATION_MANAGER_ID: 'implementationManagerId',
  THEME_ID: 'themeId',
  HSA_CUSTODIAN_ID: 'hsaCustodianId',
  DEPOSITORY_BANK_CONFIG: 'depositoryBankConfigType',
  URL: 'url',
  SSO_ONLY: 'ssoOnly',
  AMIN_SSO_ONLY: 'adminSsoOnly',
  TPA: 'tpa',
  ALLOW_EDIT_ADDRESS: 'allowEditAddress',
  // BROKER: 'broker',
};

const DBA_MAX_LENGTH = 65;
const DBA_LENGTH_MESSAGE = `DBA must have ${DBA_MAX_LENGTH} characters or less`;
const DEPOSITORY_BANK_ERROR = 'Depository bank is required';

export const SAVE_VALIDATORS = {
  [FIELD_KEY.DBA]: yup.string().trim().max(DBA_MAX_LENGTH, DBA_LENGTH_MESSAGE),
  [FIELD_KEY.TAX_ID]: yup
    .string()
    .test('len', 'Must be exactly 9 characters', (val) => !val || val.length === 9),
  [FIELD_KEY.PHONE_NUMBER]: yup.string().test(validatePhoneNumber()),
  [FIELD_KEY.PHONE_NUMBER_EXTENSION]: yup.string(),
  [FIELD_KEY.BUSINESS_TYPE]: yup.string().nullable(),
  // [FIELDS.BROKER]: yup.string(),
  [FIELD_KEY.ACCOUNT_MANAGER_ID]: yup
    .string()
    .trim()
    .test(
      'managerId',
      'One contact cannot be assigned as both account manager and implementation manager.',
      (val, { parent }) => !val || parent[FIELD_KEY.IMPLEMENTATION_MANAGER_ID] !== val
    ),
  [FIELD_KEY.IMPLEMENTATION_MANAGER_ID]: yup.string().when(FIELD_KEY.ACCOUNT_MANAGER_ID, {
    is: (val: string) => !!val,
    then: (schema) =>
      yup
        .string()
        .trim()
        .test(
          'managerId',
          'One contact cannot be assigned as both account manager and implementation manager.',
          (val, { parent }) => parent[FIELD_KEY.ACCOUNT_MANAGER_ID] !== val
        ),
  }),
  [FIELD_KEY.HSA_CUSTODIAN_ID]: yup.string(),
  [FIELD_KEY.DEPOSITORY_BANK_CONFIG]: yup.string(),
  [FIELD_KEY.URL]: yup.string().test(validateUrl()),
  [FIELD_KEY.SSO_ONLY]: yup.boolean(),
};

export const DEPOSITORY_BANK_OPTIONS: Option[] = [
  {
    key: DepositoryBankConfigTypeEnum.EMPRISE_DEPOSITORY_CONFIG,
    value: DepositoryBankConfigTypeEnum.EMPRISE_DEPOSITORY_CONFIG,
    title: 'Emprise',
  },
  {
    key: DepositoryBankConfigTypeEnum.EMPRISE_OMNIBUSES_UMB_CLEARING_DEPOSITORY_CONFIG,
    value: DepositoryBankConfigTypeEnum.EMPRISE_OMNIBUSES_UMB_CLEARING_DEPOSITORY_CONFIG,
    title: 'Emprise Omnibuses - UMB Clearing',
  },
  {
    key: DepositoryBankConfigTypeEnum.UMB_DEPOSITORY_CONFIG,
    value: DepositoryBankConfigTypeEnum.UMB_DEPOSITORY_CONFIG,
    title: 'UMB',
  },
];

const getDefaultHsaCustodiansValue = (hsaCustodians: Option[], value?: string) => {
  if (!value) {
    return '';
  }

  return (
    hsaCustodians.find((hsaCustodianId) => `${hsaCustodianId.value}`.toLowerCase() === value.toLowerCase())
      ?.key || ''
  );
};

const useGeneralInformationFields = (
  managers: Option[],
  businessTypes: Option[],
  hsaCustodians: Option[],
  activateMode?: boolean,
  data?: EmployerDto,
  isLoading?: boolean,
  triggeredId?: string
) => {
  const taxIdProps = useGetPermissionToEditOverridableField(PermissionKey.TAX_ID, FieldStateKey.TAX_ID);
  // const implementationManager = useGetPermissionToEditOverridableField(
  //   PermissionKey.IMPLEMENTATION_MANAGER_ID,
  //   FieldStateKey.IMPLEMENTATION_MANAGER_ID
  // );

  const urlPermProps = useGetPermissionToEditOverridableField(PermissionKey.URL, FieldStateKey.URL);
  const hsaCustodianIdPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.HSA_CUSTODIAN_TYPE,
    FieldStateKey.HSA_CUSTODIAN_TYPE
  );
  const isDepositoryBankConfigEnabled = useFeatureFlag(FeatureFlags.DEPOSITORY_BANK_CONFIG);
  const canModifyDepositoryBank = useHasAccess([{ permission: Permissions.MODIFY_DEPOSITORY_CUSTODIAN_BANK }]);
  const depositoryBankConfigPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.DEPOSITORY_BANK_CONFIG_TYPE,
    FieldStateKey.DEPOSITORY_BANK_CONFIG_TYPE
  );
  const ssoOnlyPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.SSO_ONLY,
    FieldStateKey.SSO_ONLY
  );
  const adminSsoOnlyPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.ADMIN_SSO_ONLY,
    FieldStateKey.ADMIN_SSO_ONLY
  );
  const isAllowEmployeesToEditAddressPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.ALLOW_EMPLOYEES_TO_EDIT_ADDRESS,
    FieldStateKey.ALLOW_EMPLOYEES_TO_EDIT_ADDRESS
  );

  const [invalidPhoneNumber, setInvalidPhoneNumber] = useState('');

  const generalInfo = useStore((state) => state.generalInfo);
  const errorsState = useErrorStore((state) => state.state);
  const phoneError = useMemo(() => {
    const formatedError = _.get(errorsState, 'organization_phone.number', '');
    return formatedError;
  }, [errorsState]);

  useEffect(() => {
    if (phoneError) {
      setInvalidPhoneNumber(generalInfo?.phoneNumber || '');
    }
  }, [phoneError]);
  const { employerID, mode } = useSetupEmployerMode();
  const isSubsidiary = mode === EmployerSetupModes.subsidiary;

  const isDepositoryBankConfigLockedByFe = isSubsidiary
    || !!data?.isActivationStarted
    || (!!data?.organizationStatusType
      && data?.organizationStatusType !== EmployerStatus.Pending
      && data?.organizationStatusType !== EmployerStatus.ExpressSetup);
  const fields = useMemo(() => {
    if (isLoading) return [];

    const phoneNumber = (data?.organizationPhone?.number || '').replace(REGEXPS.EXCLUDE_NUMBERS, '');
    const phoneExtension = data?.organizationPhone?.phoneExtension || '';
    return [
      {
        name: FIELD_KEY.DBA,
        type: FieldTypes.Text,
        label: 'Legal name',
        placeholder: 'Legal name',
        validator: yup.string().trim().max(DBA_MAX_LENGTH, DBA_LENGTH_MESSAGE),
        value: data?.shortName || '', // generalInfo?.dba,
        defaultValue: data?.shortName || '', // generalInfo?.dba,
      },
      {
        name: FIELD_KEY.TAX_ID,
        type: FieldTypes.Mask,
        mask: '999999999',
        label: 'Tax ID (EIN)',
        placeholder: 'Enter Tax ID (EIN)',
        validator: yup
          .string()
          .test('len', 'Must be exactly 9 characters', (val) => !val || val.length === 9),
        // showRequireIcon: true,
        // @ts-ignore: Unreachable code error
        value: data?.normalProperties?.taxId || '', // generalInfo?.taxID,
        defaultValue: data?.normalProperties?.taxId || '', // generalInfo?.taxID,
        ...taxIdProps,
      },
      {
        name: FIELD_KEY.PHONE_NUMBER,
        type: FieldTypes.PhoneNumber,
        label: 'Company contact phone number',
        placeholder: '+1 (555) 555-1234',
        validator: yup.string().test(validatePhoneNumber()),
        value: phoneNumber,
        defaultValue: phoneNumber,
        subFields: [
          {
            name: FIELD_KEY.PHONE_NUMBER_EXTENSION,
            type: FieldTypes.Text,
            parentValueFn: (val: string) => !!val && validatePhoneNumber().test(`${val || ''}`),
            label: 'Phone extension',
            placeholder: 'Enter phone extension',
            validator: yup.string().trim().nullable(),
            value: phoneExtension,
            defaultValue: phoneExtension,
          },
        ],
      },
      {
        name: FIELD_KEY.BUSINESS_TYPE,
        type: FieldTypes.AutocompleteDropdown,
        label: 'Business entity types',
        placeholder: 'Select Business entity types',
        singleMode: true,

        validator: yup.string().nullable(),
        options: businessTypes.map((businessType) => ({
          ...businessType,
          title: businessType?.value?.toString(),
        })),
        value: data?.businessEntityType, // generalInfo?.businessType,
        defaultValue: data?.businessEntityType, // generalInfo?.businessType,
      },
      // // {
      // //   name: FIELDS.BROKER,
      // //   type: FieldTypes.Text,
      // //   label: 'Broker',
      // //   placeholder: 'Enter a broker',
      // //   validator: yup.string(),
      // //   value: generalInfo?.broker,
      // //   defaultValue: generalInfo?.broker,
      // // },
      {
        name: FIELD_KEY.ACCOUNT_MANAGER_ID,
        type: FieldTypes.AutocompleteDropdown,
        label: 'Account manager',
        placeholder: 'Select an account manager',
        validator: SAVE_VALIDATORS[FIELD_KEY.ACCOUNT_MANAGER_ID],
        // optional: true,
        options: managers.map((manager) => ({
          ...manager,
          title: manager?.value?.toString(),
        })),

        value: data?.normalProperties?.accountManagerId || '',
        defaultValue: data?.normalProperties?.accountManagerId || '',
      },
      // {
      //   name: FIELD_KEY.IMPLEMENTATION_MANAGER_ID,
      //   type: FieldTypes.AutocompleteDropdown,
      //   label: 'Implementation manager',
      //   placeholder: 'Select implementation manager',
      //   validator: SAVE_VALIDATORS[FIELD_KEY.IMPLEMENTATION_MANAGER_ID],
      //   // optional: true,
      //   options: managers.map((manager) => ({
      //     ...manager,
      //     title: manager?.value?.toString(),
      //   })),

      //   value: data?.normalProperties?.implementationManagerId || '',
      //   defaultValue: data?.normalProperties?.implementationManagerId || '',
      //   ...implementationManager,
      // },
      ...(isDepositoryBankConfigEnabled && canModifyDepositoryBank ? [{
        name: FIELD_KEY.DEPOSITORY_BANK_CONFIG,
        type: FieldTypes.AutocompleteDropdown,
        label: (
          <Box flex={{ grow: 1 }}>
            <Inscription margin={{ bottom: '4px' }} weight="bold" color="textBody">
              Depository bank
            </Inscription>
            <Box margin={!isDepositoryBankConfigLockedByFe ? { left: '-12px' } : undefined}>
              <Inscription weight="normal" color="textSecondary" size="small">
                Select the bank configuration where funds will be held and processed
              </Inscription>
            </Box>
          </Box>
        ),
        placeholder: 'Select depository bank',
        validator: isDepositoryBankConfigLockedByFe
          ? yup.string()
          : yup.string().required(DEPOSITORY_BANK_ERROR),
        showRequireIcon: !isDepositoryBankConfigLockedByFe,
        options: DEPOSITORY_BANK_OPTIONS,
        value: data?.overridableProperties?.depositoryBankConfigType || '',
        defaultValue: data?.overridableProperties?.depositoryBankConfigType || '',
        ...depositoryBankConfigPermProps,
        ...(isDepositoryBankConfigLockedByFe ? { disabled: true } : {}),
      } as Field] : []),
      {
        name: FIELD_KEY.HSA_CUSTODIAN_ID,
        type: FieldTypes.AutocompleteDropdown,
        label: 'HSA custodian',
        placeholder: 'Select HSA custodian',

        validator: yup.string(),
        optional: true,
        options: hsaCustodians.map((hsaCustodian) => ({
          ...hsaCustodian,
          title: hsaCustodian?.value?.toString(),
        })),
        value: getDefaultHsaCustodiansValue(hsaCustodians, data?.overridableProperties?.hsaCustodianId),
        defaultValue: getDefaultHsaCustodiansValue(
          hsaCustodians,
          data?.overridableProperties?.hsaCustodianId
        ),
        ...hsaCustodianIdPermProps,
      },
      {
        name: FIELD_KEY.URL,
        type: FieldTypes.Text,
        label: 'URL',
        placeholder: 'URL',
        validator: VALIDATORS.STRING,
        value: data?.overridableProperties?.url || '',
        defaultValue: data?.overridableProperties?.url || '',
        ...urlPermProps,
      },
      {
        name: FIELD_KEY.SSO_ONLY,
        type: FieldTypes.Checkbox,
        testId: 'sso-only',
        label: 'Participant portal SSO only',
        validator: yup.boolean().nullable(),
        value: data?.overridableProperties?.ssoOnly || false,
        defaultValue: data?.overridableProperties?.ssoOnly || false,
        ...ssoOnlyPermProps,
      },
      {
        name: FIELD_KEY.AMIN_SSO_ONLY,
        type: FieldTypes.Checkbox,
        testId: 'admin-sso-only',
        label: 'Admin portal SSO only',
        validator: yup.boolean().nullable(),
        value: data?.overridableProperties?.adminSsoOnly || false,
        defaultValue: data?.overridableProperties?.adminSsoOnly || false,
        ...adminSsoOnlyPermProps,
      },
      {
        name: FIELD_KEY.ALLOW_EDIT_ADDRESS,
        type: FieldTypes.Radio,
        testId: 'allowEditAddress',
        label: 'Allow employees to edit address',
        validator: VALIDATORS.STRING,
        options: [
          {
            label: 'Yes',
            value: YES_KEY,
          },
          {
            label: 'No',
            value: NO_KEY,
          },
        ],
        value: booleanToYesOrNo(data?.overridableProperties?.allowEmployeesEditAddress) || NO_KEY,
        defaultValue: booleanToYesOrNo(data?.overridableProperties?.allowEmployeesEditAddress) || NO_KEY,
        ...isAllowEmployeesToEditAddressPermProps,
      },
    ] as Field[];
  }, [
    invalidPhoneNumber,
    managers,
    businessTypes,
    isLoading,
    employerID,
    generalInfo?.hsaCustodianId,
    isDepositoryBankConfigEnabled,
    canModifyDepositoryBank,
    isDepositoryBankConfigLockedByFe,
    triggeredId,
  ]);
  const fieldsWithValues = useMemo(() => {
    const list = [...fields];
    if (!activateMode) {
      return list.map((field) => {
        // Depository bank keeps its inline (FE-lock aware) required validator on save too,
        // so an empty value blocks Save the same way it blocks Activate.
        if (field.name === FIELD_KEY.DEPOSITORY_BANK_CONFIG) {
          return field;
        }
        const validator = SAVE_VALIDATORS[field.name];
        return {
          ...field,
          validator,
        };
      });
    }
    return list;
  }, [activateMode, fields, triggeredId]);

  const formattedFields = useHideFields(fieldsWithValues);
  return formattedFields;
};

export default useGeneralInformationFields;
